/**
 * Declaring where propagation material comes from.
 *
 * Used by a nursery on an offer and by a grower asking to propagate its own
 * trees. The declaration is the double check that what gets grafted is the
 * cultivar the licence is for, so every row has to name somewhere a person
 * could go and look — a scheme and lot number, a block and its rows, a named
 * supplier. "Own stock" with no further detail checks nothing.
 */

import { useId } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  MATERIAL_KIND_LABELS,
  SOURCE_TYPE_LABELS,
  SOURCE_NAME_PLACEHOLDERS,
  blankMaterialSource,
  type MaterialKind,
  type MaterialSourceInput,
  type MaterialSourceType,
} from '../../types/propagation';

interface Props {
  value: MaterialSourceInput[];
  onChange: (next: MaterialSourceInput[]) => void;
  /** Shown as a requirement when the declaration is about to be relied on. */
  requireScion?: boolean;
  disabled?: boolean;
}

export default function MaterialSourcesEditor({ value, onChange, requireScion = false, disabled = false }: Props) {
  const baseId = useId();

  const update = (index: number, patch: Partial<MaterialSourceInput>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const remove = (index: number) => onChange(value.filter((_, i) => i !== index));

  const hasScion = value.some(s => s.material_kind === 'scion');

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm font-semibold text-neutral-700">
          Where the propagation material comes from{requireScion ? ' *' : ''}
        </p>
        <p className="text-xs text-neutral-500">
          The check that what gets grafted is the right cultivar. Name somewhere a person could go
          and look: the scheme and lot, the block and rows, the supplier.
        </p>
      </div>

      {requireScion && !hasScion && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          A scion (budwood) source is required — it is the licensed material itself.
        </p>
      )}

      {value.length === 0 ? (
        <p className="text-sm text-neutral-500">No sources recorded yet.</p>
      ) : (
        <div className="space-y-3">
          {value.map((row, index) => {
            const id = (field: string) => `${baseId}-${index}-${field}`;
            return (
              <div key={index} className="border border-neutral-200 rounded-lg p-3 bg-white space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label htmlFor={id('kind')} className="block text-xs font-semibold text-neutral-700">
                      Material
                    </label>
                    <select
                      id={id('kind')}
                      disabled={disabled}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={row.material_kind}
                      onChange={e => update(index, { material_kind: e.target.value as MaterialKind })}
                    >
                      {(Object.keys(MATERIAL_KIND_LABELS) as MaterialKind[]).map(k => (
                        <option key={k} value={k}>{MATERIAL_KIND_LABELS[k]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label htmlFor={id('type')} className="block text-xs font-semibold text-neutral-700">
                      Source type
                    </label>
                    <select
                      id={id('type')}
                      disabled={disabled}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      value={row.source_type}
                      onChange={e => update(index, { source_type: e.target.value as MaterialSourceType })}
                    >
                      {(Object.keys(SOURCE_TYPE_LABELS) as MaterialSourceType[]).map(t => (
                        <option key={t} value={t}>{SOURCE_TYPE_LABELS[t]}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label htmlFor={id('name')} className="block text-xs font-semibold text-neutral-700">
                      Source
                    </label>
                    <input
                      id={id('name')}
                      disabled={disabled}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      placeholder={SOURCE_NAME_PLACEHOLDERS[row.source_type]}
                      value={row.source_name}
                      onChange={e => update(index, { source_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor={id('ref')} className="block text-xs font-semibold text-neutral-700">
                      Lot, batch or certificate no.
                    </label>
                    <input
                      id={id('ref')}
                      disabled={disabled}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      placeholder="Optional, but it is what gets checked"
                      value={row.source_reference || ''}
                      onChange={e => update(index, { source_reference: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label htmlFor={id('notes')} className="block text-xs font-semibold text-neutral-700">
                    Notes{row.source_type === 'other' ? ' *' : ''}
                  </label>
                  <input
                    id={id('notes')}
                    disabled={disabled}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                    placeholder={row.source_type === 'other' ? 'What this source is' : 'Anything that helps confirm it'}
                    value={row.notes || ''}
                    onChange={e => update(index, { notes: e.target.value })}
                  />
                </div>

                {!disabled && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-xs text-red-600 underline inline-flex items-center gap-1"
                  >
                    <Trash2 className="h-3 w-3" /> Remove source
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!disabled && (
        <button
          type="button"
          onClick={() => onChange([...value, blankMaterialSource(hasScion ? 'rootstock' : 'scion')])}
          className="text-sm text-blue-600 underline inline-flex items-center gap-1"
        >
          <Plus className="h-4 w-4" /> Add a source
        </button>
      )}
    </div>
  );
}
